import { NgStyle } from "@angular/common";
import {
    Component,
    ElementRef,
    HostListener,
    OnDestroy,
    OnInit,
    Renderer2,
    ViewChild,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { BusyService } from "@components/busy/busy.service";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { InputComponent } from "@components/input/input.component";
import { MapLayersComponent } from "@components/map/components/menu/map-layers/map-layers.component";
import { MapComponent } from "@components/map/map.component";
import { PageComponent } from "@components/page/page.component";
import { RadiobuttonGroupComponent } from "@components/radiobuttongroup/radiobuttongroup.component";
import { SaveButtonComponent } from "@components/save-button/save-button.component";
import { TogglePanelComponent } from "@components/toggle-panel/toggle-panel.component";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { Observable, Subject, of } from "rxjs";
import { shareReplay, takeUntil, tap } from "rxjs/operators";
import { ColorPickerModalComponent } from "~app/common/components/color-picker/color-picker-modal/color-picker-modal.component";
import { MapDrawComponent } from "~app/common/components/map/components/menu/map-draw/map-draw.component";
import { IMapComponent, IMapMouse } from "~app/common/components/map/map.models";
import {
    IFilterParams,
    IMapGeometry,
    IMapGeometryWicket,
    IMapSettings,
    IRenderParams
} from "~app/common/components/map/map.settings";
import { TransportomradenConstats } from "~app/common/models/constants";
import { TransportomradeDTO } from "~app/common/models/kontakter/TransportomradeDTO";
import { TplTransportforetagDTO } from "~app/common/models/transport/TplTransportforetagDTO";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { DirtyFormService } from "~app/common/services/dirty-form.service";
import { CanBeDirty } from "~app/common/guards/dirty.guard";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { ModelActionService } from "../../../../common/services/cqs/model-action.service";
import { FormBinderService } from "../../../../common/services/form-binder.service";
import { TransportforetagRedigeraService } from "../services/transportforetag-redigera.service";
import { TransportforetagService } from "../services/transportforetag.service";
import { TransportforetagRedigeraPopupComponent } from "../transportforetag-redigera-popup/transportforetag-redigera-popup.component";
import { ITransportforetagRedigeraPopupData } from "../transportforetag-redigera-popup/transportforetag-redigera.model";

@Component({
    selector: "rask-transportforetag-redigera",
    templateUrl: "./transportforetag-redigera.component.html",
    styleUrls: ["./transportforetag-redigera.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        PageComponent,
        TogglePanelComponent,
        FormsModule,
        ReactiveFormsModule,
        NgStyle,
        InputComponent,
        CheckboxComponent,
        RadiobuttonGroupComponent,
        MapComponent,
        MapLayersComponent,
        SaveButtonComponent,
        MapDrawComponent
    ]
})

export class TransportforetagRedigeraComponent implements OnInit, OnDestroy, CanBeDirty {
    colors: string[];
    filter = new Subject<IFilterParams>();
    @ViewChild("footer", { static: true }) footer: ElementRef;
    formTransportforetag = new UntypedFormGroup({
        transportkapacitet: new UntypedFormControl(""),
        transportunderlagTillExtern: new UntypedFormControl("")
    });
    @ViewChild("header", { static: true }) header: ElementRef;
    @ViewChild("infowindow", { static: true }) infowindow: ElementRef;
    map: IMapComponent;
    @ViewChild("mapcontainer", { static: true }) mapcontainer: ElementRef;
    mapsettings: IMapSettings;
    otherTransportforetag: TplTransportforetagDTO[];
    refresh = new Subject<IRenderParams>();
    selectedOmrade: TransportomradeDTO;
    selectedTransportforetag: TplTransportforetagDTO;
    transportforetag: TplTransportforetagDTO;
    transportomraden: TransportomradeDTO[];
    transportsatt = 1;

    private readonly redigeraLagerId = "redigera";
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly busyService = inject(BusyService);
    private readonly dirtyFormService = inject(DirtyFormService);
    private readonly formBinder = inject(FormBinderService);
    private readonly modelActionService = inject(ModelActionService);
    private readonly newgeometries: IMapGeometryWicket[] = [];
    private readonly ngbModal = inject(NgbModal);
    private readonly ownYtorOpacity = 0.3;
    private readonly renderer = inject(Renderer2);
    private readonly transportforetagService = inject(TransportforetagService);
    private readonly transportforetagRedigeraService = inject(TransportforetagRedigeraService);
    private readonly authenticationService = inject(AuthenticationService);
    private readonly destroyed$ = new Subject<void>();

    canSave() {
        return this.formTransportforetag.dirty;
    }

    canEdit() {
        return this.authenticationService.canEdit();
    }

    readonly isDirtyFn = (): Promise<boolean> => new Promise(resolve => {
        if (!this.canSave()) {
            resolve(true);
            return;
        }

        this.dirtyFormService.dirtyFormModal(
            () => {
                this.discardChanges();
                resolve(true);
            },
            () => {
                this.save().subscribe({
                    next: () => resolve(true),
                    error: () => resolve(false)
                });
            },
            () => {
                resolve(false);
            }
        );
    });

    onBeforeChangeTransportsatt = (newKey: number): Observable<boolean> => {
        if (newKey === this.transportsatt || !this.canSave()) {
            return of(true);
        }

        const subject = new Subject<boolean>();
        this.dirtyFormService.dirtyFormModal(
            () => {
                this.discardChanges();
                subject.next(true);
                subject.complete();
            },
            () => {
                this.save().subscribe({
                    next: () => {
                        subject.next(true);
                        subject.complete();
                    },
                    error: () => {
                        subject.next(false);
                        subject.complete();
                    }
                });
            },
            () => {
                subject.next(false);
                subject.complete();
            }
        );

        return subject.asObservable();
    };

    geometryChanged(geom: IMapGeometryWicket) {
        const to = this.createOrUpdateOmrade(geom);
        if (to.id) {
            this.modelActionService.addNewChildModel(to, "Transportomrade", this.transportforetag, "id");
        }
        this.selectedOmrade = null;
        this.formTransportforetag.markAsDirty();
    }

    geometryCreated(geom: IMapGeometryWicket) {
        this.createOrUpdateOmrade(geom);
        this.selectedOmrade = null;
        this.formTransportforetag.markAsDirty();
    }

    geometryDeleted(geometries: IMapGeometry[]): boolean {
        geometries.forEach((geometry) => {
            if (geometry.id) {
                const omrade = this.transportomraden.find(x => x.id === geometry.id);
                this.modelActionService.removeModel(omrade, "Transportomrade", this.transportforetag);
                this.transportomraden.splice(this.transportomraden.indexOf(omrade), 1);
                this.formTransportforetag.markAsDirty();
                this.map.updateGeometry(geometry, this.redigeraLagerId, true);
                const layer = this.getRedigeraLager();
                const index = layer?.geometries?.findIndex(x => x.id === geometry.id);
                if (index > -1) {
                    layer.geometries.splice(index, 1);
                }
            } else {
                const omrade = this.newgeometries.find(x => x.geoid === geometry.geoid);
                this.newgeometries.splice(this.newgeometries.indexOf(omrade), 1);
            }
        });
        this.selectedOmrade = null;
        this.refresh.next({ layerId: this.redigeraLagerId });
        return true;
    }

    getAktivText(aktiv = false) {
        if (!aktiv) {
            return "";
        }
        return aktiv === true ? "Ja" : "Nej";
    }

    gotoOwngeometries() {
        this.map?.gotoAll([this.redigeraLagerId]);
    }

    initMapSettings() {
        this.mapsettings = {
            id: "transportforetagkarta",
            center: [
                17.9531,
                59.3565
            ],
            zoom: 0,
            coords: true,
            zoomButtons: true,
            onLayerReady: (m, l) => this.layerReady(m, l),
            onMapClick: (mouse, hitlist) => this.onMapClick(mouse, hitlist),
            baselayers: [
                {
                    id: "metriabakgrund",
                    visible: true
                },
                {
                    id: "metriaorto",
                    visible: true
                }
            ],
            featurelayers: [
                {
                    id: "VerkGemAdminGrans",
                    visible: true
                },
                {
                    id: "VerkGemLLD",
                    visible: true
                },
                {
                    id: "VerkGemLKF",
                    visible: true
                },
                {
                    id: "VerkGemFastigheter",
                    visible: true
                },
                { id: "Transportomraden" },
                { id: "Vagar" },
                { id: "VFS_PlaneradVag" }
            ],
            drawLayers: [
                {
                    id: this.redigeraLagerId,
                    readonly: true,
                    title: "Redigera Transportområden",
                    geometries: [],
                    geometryTypes: ["polygon"],
                    color: [
                        0,
                        0,
                        0,
                        0.2
                    ],
                    visible: true,
                    allowInvisibleGeometries: false,
                    showGeometriesMenu: true,
                    onClick: (mouseevent: IMapMouse, geom: IMapGeometry) => {
                        this.transportomradeLayerClick(mouseevent, geom);
                        return false;
                    },
                    drawOptions: [
                        {
                            title: `Skapa nytt område`,
                            type: "polygon",
                            callback: (g) => this.geometryCreated(g),
                            snapLayerId: this.redigeraLagerId
                        }
                    ]
                },
                {
                    id: "othertransportforetag",
                    visible: true,
                    readonly: true,
                    title: "Andra Transportföretag",
                    geometries: [],
                    color: [
                        0,
                        0,
                        0,
                        1
                    ],
                    onClick: (mouseevent: any, geom: IMapGeometry) => {
                        this.otherForetagLayerClick(geom, mouseevent);
                        return false;
                    }
                }
            ],

            icons: [
                {
                    id: "truck",
                    color: "black",
                    arcgisStyle: "path",
                    size: 12,
                    iconSvgPath: "M624 352h-16V243.9c0-12.7-5.1-24.9-14.1-33.9L494 110.1c-9-9-21.2-14.1-33.9-14.1H416V48c0-26.5-21.5-48-48-48H48C21.5 0 0 21.5 0 48v320c0 26.5 21.5 48 48 48h16c0 53 43 96 96 96s96-43 96-96h128c0 53 43 96 96 96s96-43 96-96h48c8.8 0 16-7.2 16-16v-32c0-8.8-7.2-16-16-16zM160 464c-26.5 0-48-21.5-48-48s21.5-48 48-48 48 21.5 48 48-21.5 48-48 48zm320 0c-26.5 0-48-21.5-48-48s21.5-48 48-48 48 21.5 48 48-21.5 48-48 48zm80-208H416V144h44.1l99.9 99.9V256z"
                }
            ]
        };
        this.setMapHeight();
    }

    layerReady(map: IMapComponent, layerid: string) {
        this.map = map;
        if (layerid === "Transportomraden") {
            setTimeout(() => {
                this.signalTransportsattChanged();
            }, 200);
        }
        if (layerid === this.redigeraLagerId) {
            const redigera = this.getRedigeraLager();
            redigera.color = this.getColor(this.transportforetag.color, this.ownYtorOpacity);
            this.refresh.next({ layerId: this.redigeraLagerId });
            this.gotoOwngeometries();
        }
        if (layerid === "othertransportforetag") {
            this.refresh.next({ layerId: "othertransportforetag" });
        }
    }

    loadColors() {
        this.transportforetagService.getTransportforetagColors().subscribe(colors => {
            this.colors = colors;
        });
    }

    loadOtherTransportomraden(exceptid: any) {
        this.transportforetagService.getAllTransportforetagWithGeometryExcept(exceptid).subscribe(result => {
            this.otherTransportforetag = result;
        });
    }

    loadOwnGeometries(id: number) {
        this.transportforetagService.getOmraden(id).subscribe(result => {
            this.transportomraden = result;
            this.modelActionService.bindModel(this.transportomraden, "Transportomrade", "id");
            this.mapOwnGeometries();
            this.showRedigeraYtor();
            this.gotoOwngeometries();
        });
    }

    loadTransportforetag(id: number) {
        this.transportforetagService.get(id).subscribe(result => {
            this.transportforetag = result;
            this.formTransportforetag = this.formBinder.CreateGroup(this.transportforetag);
            if (!this.transportforetag.aktiv) {
                this.formTransportforetag.disable();
            }
            this.modelActionService.bindModel(this.transportforetag, "Transportforetag");
        });
    }

    ngOnInit() {

        const id = +this.activatedRoute.snapshot.paramMap.get("id");
        this.initMapSettings();
        this.modelActionService.reset();
        this.loadTransportforetag(id);
        this.loadOwnGeometries(id);
        this.loadOtherTransportomraden(id);
        this.loadColors();
        this.setupRedigeraEventHandlers();
    }

    ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    onMapClick(mouse, hitlist) {
        if (this.map.isWidgetsActive(["map-layers"])) {
            this.map.closeWidgets();
        }
    }

    @HostListener("window:resize", ["$event"])
    onResize(event) {
        this.setMapHeight();
    }

    onToggleHeader($event) {
        setTimeout(() => {
            this.setMapHeight();
        }, 100);
    }

    openColorPicker() {
        const modalRef = this.ngbModal.open(ColorPickerModalComponent, {
            size: "sm",
            backdrop: "static"
        });
        modalRef.componentInstance.color = this.transportforetag.color;
        modalRef.componentInstance.colors = this.colors;
        modalRef.result.then(color => {
            if (color) {
                const layer = this.getRedigeraLager();
                const geometries = layer.geometries as IMapGeometryWicket[];
                geometries.forEach(g => {
                    g.color = this.getColor(color, this.ownYtorOpacity);
                });
                this.transportforetag.color = color;
                this.formTransportforetag.markAsDirty();
                this.refresh.next({ layerId: this.redigeraLagerId });
            }
        });
    }

    otherForetagLayerClick(geo: IMapGeometry, mouseevent: any) {
        if (geo) {
            this.selectedTransportforetag = this.otherTransportforetag.find(x => x.id === geo.id);
            this.displayInfowindow(mouseevent.x, mouseevent.y);
        } else {
            this.hideInfowindow();
        }
    }

    onSave() {
        this.save().subscribe();
    }

    save(): Observable<any> {
        this.createOmradenFromNewGeometries();
        return this.busyService.addWork(this.modelActionService.pushCommandQueue()).pipe(
            tap(() => {
                this.mapOwnGeometries();
                const layer = this.getRedigeraLager();
                layer.color = this.getColor(this.transportforetag.color, this.ownYtorOpacity);
                this.refresh.next({ layerId: this.redigeraLagerId });
                this.formTransportforetag.markAsPristine();
                this.transportforetagService.getAllTransportforetag(true).subscribe();
                this.signalTransportsattChanged();
            }),
            shareReplay(1)
        );
    }

    showSelected() {
        if (this.selectedOmrade) {
            return `${this.selectedOmrade.lopnummer}. ${this.getTransportsatt(this.selectedOmrade)}`;
        } else {
            return "";
        }
    }

    transportomradeLayerClick(mouse: IMapMouse, geometry: IMapGeometry) {
        if (ObjectUtils.isNullOrUndefined(geometry)) {
            return;
        }
        this.selectedOmrade = this.transportomraden.find(x => x.id === geometry.id);
        const layer = this.getRedigeraLager();
        this.showKartgeometriPopup(mouse, geometry,
            {
                geometri: geometry,
                layer: layer,
                canEdit: this.canEdit()
            });
    }

    transportsattChanged() {
        this.signalTransportsattChanged();
    }

    private setupRedigeraEventHandlers() {
        this.transportforetagRedigeraService.deleteTransportomrade$
            .pipe(takeUntil(this.destroyed$))
            .subscribe((geo) => {
                this.geometryDeleted([geo]);
            });
        this.transportforetagRedigeraService.editTransportomrade$
            .pipe(takeUntil(this.destroyed$))
            .subscribe((geo) => {
                this.geometryChanged(geo);
            });
    }

    private showKartgeometriPopup(mouse: IMapMouse, geometry: IMapGeometry, data: ITransportforetagRedigeraPopupData) {
        this.map.showPopupOnGeometry(TransportforetagRedigeraPopupComponent, geometry, data);
    }

    private getRedigeraLager() {
        return this.mapsettings.drawLayers.find(x => x.id === this.redigeraLagerId);
    }

    private createOmradenFromNewGeometries() {
        this.newgeometries.forEach(geo => {
            const dto = {
                id: null,
                geometri: geo.wkt,
                omradeFlaggor: this.transportsatt
            } as TransportomradeDTO;

            this.transportomraden.push(dto);
            this.modelActionService.addNewChildModel(dto, "Transportomrade", this.transportforetag, "id");
        });
        this.newgeometries.splice(0, this.newgeometries.length);
    }

    private createOrUpdateOmrade(mapGeo: IMapGeometryWicket) {
        const t = this.transportomraden.find(x => x.id === mapGeo.id);
        if (t != null) {
            t.geometri = mapGeo.wkt;
            return t;
        }
        const g = this.newgeometries.find(x => x.geoid === mapGeo.geoid);
        if (g != null) {
            g.wkt = mapGeo.wkt;
            return g;
        }
        this.newgeometries.push(mapGeo);
        return mapGeo;
    }

    private displayInfowindow(x: number, y: number) {
        const element = this.infowindow.nativeElement;
        this.renderer.setStyle(element, "top", `${y}px`);
        this.renderer.setStyle(element, "left", `${x}px`);
        this.renderer.setStyle(element, "display", "block");
    }

    private filterTransportomraden() {
        const filter = [];
        if (this.transportsatt === TransportomradenConstats.Rundvirke) {
            filter.push("Transportsatt = 'Rundvirke'");
        }
        if (this.transportsatt === TransportomradenConstats.Bransle) {
            filter.push("Transportsatt ='Bransle'");
        }
        const filterExpression = `Transportforetag_GUID <> '${this.transportforetag?.guid}' AND (${filter.join(" OR ")})`;
        this.map.filterLayer("Transportomraden", filterExpression, ["Transportområdeytor"]);
    }

    private getColor(hex: string, alpha = 1): any {
        if (hex == null || hex.indexOf("#") !== 0) {
            return hex;
        }
        hex = hex.replace("#", "");
        return [
            parseInt(hex.substring(0, 2), 16),
            parseInt(hex.substring(2, 4), 16),
            parseInt(hex.substring(4, 6), 16),
            alpha
        ];
    }

    private getTransportsatt(omrade: TransportomradeDTO) {
        const b = [];
        if (this.isRundvirke(omrade.omradeFlaggor)) {
            b.push("Rundvirke");
        }
        if (this.isBransle(omrade.omradeFlaggor)) {
            b.push("Bränsle");
        }
        return b.join(",");
    }

    private hideInfowindow() {
        const element = this.infowindow.nativeElement;
        this.renderer.setStyle(element, "display", "none");
    }

    private isBransle(flags: number): boolean {
        // eslint-disable-next-line no-bitwise
        return (flags & 2) === 2;
    }

    private isRundvirke(flags: number): boolean {
        // eslint-disable-next-line no-bitwise
        return (flags & 1) === 1;
    }

    private mapOwnGeometries() {
        const redigeraLayer = this.getRedigeraLager();
        const transportomraden = this.transportomraden.map((to) => {
            const isRund = this.isRundvirke(to.omradeFlaggor);
            const isBransl = this.isBransle(to.omradeFlaggor);
            const visible = (this.transportsatt === TransportomradenConstats.Rundvirke && isRund) ||
                (this.transportsatt === TransportomradenConstats.Bransle && isBransl);
            return {
                wkt: to.geometri,
                id: to.id,
                label: isRund ? "Rundvirke" : "Bränsle",
                color: this.getColor(this.transportforetag?.color, this.ownYtorOpacity),
                visible: visible,
                tag: to
            };
        });
        redigeraLayer.geometries = transportomraden;
    }

    private setMapHeight() {
        const mapCalculatedHeight = window.innerHeight - 226;
        const height = `${mapCalculatedHeight}px`;
        this.mapsettings.height = height;
        this.map?.setMapHeight(height);
    }

    private showOthertransportforetagIcons() {
        const layer = this.mapsettings.drawLayers.find(x => x.id === "othertransportforetag");
        const trucks = this.transportforetagService.getGeometriesForTransportforetag(this.otherTransportforetag, this.transportsatt);

        this.map.setGeometries(layer.id, trucks);
    }

    private showRedigeraYtor() {
        const layer = this.getRedigeraLager();
        layer.geometries.forEach(geometry => {
            geometry.visible = (this.transportsatt === TransportomradenConstats.Rundvirke && this.isRundvirke(geometry.tag?.omradeFlaggor) ||
                this.transportsatt === TransportomradenConstats.Bransle && this.isBransle(geometry.tag?.omradeFlaggor));
            geometry.label = this.isRundvirke(geometry.tag?.omradeFlaggor) ? "Rundvirke" : "Bränsle";
        });

        this.refresh.next({ layerId: this.redigeraLagerId });
    }

    private signalTransportsattChanged() {
        this.selectedOmrade = null;
        this.hideInfowindow();
        this.map?.closePopups();
        this.map?.getSketch()?.cancelSketch();
        this.filterTransportomraden();
        this.showRedigeraYtor();
        this.showOthertransportforetagIcons();
    }

    private discardChanges() {
        this.selectedOmrade = null;
        this.hideInfowindow();
        this.map?.closePopups();
        this.map?.getSketch()?.cancelSketch();
        this.newgeometries.splice(0, this.newgeometries.length);
        this.modelActionService.reset();
        if (this.transportforetag) {
            this.formTransportforetag.reset(this.transportforetag);
            this.modelActionService.bindModel(this.transportforetag, "Transportforetag");
        }
        this.formTransportforetag.markAsPristine();
        if (this.transportforetag?.id) {
            this.loadOwnGeometries(this.transportforetag.id);
            this.loadTransportforetag(this.transportforetag.id);
        }
    }
}
